import { api } from './api-client';

/**
 * One voice the deployment's synthesiser can reach.
 *
 * Declared here rather than imported from the API: ADR 0001 settles that types
 * are duplicated between the API and its clients rather than shared through a
 * workspace. The shape has to keep matching `api/src/speech/`.
 *
 * One entry per voice *per language* — a voice offered in two locales arrives
 * twice, because what a picker needs is "voices for this language" and that is
 * the row that answers it.
 */
export interface SpeechVoice {
  /** The provider name, and the only string that ends up in a column. */
  name: string;
  /** BCP-47, carrying a region: `es-ES`, never `es`. */
  languageCode: string;
  /** `male`, `female` or `neutral`. */
  gender: string;
  /** The family the name encodes — `Neural2`, `Standard`, `Chirp3-HD`. */
  tier: string;
}

/**
 * Every voice this deployment can reach.
 *
 * Listed live rather than from a constant, the same way the model settings
 * screen lists models: a voice the provider adds is offerable the day it ships.
 * It is also why the fields that hold a voice are pickers rather than text
 * boxes — the provider does **not** reliably reject an unknown voice name, so a
 * typo would silently produce some other voice instead of an error.
 *
 * Throws `SPEECH_NOT_CONFIGURED` where the deployment has no synthesiser, which
 * callers should show as "no synthesiser configured" rather than as an empty
 * list. An empty list reads as "this provider has no voices".
 */
export async function listVoices(): Promise<SpeechVoice[]> {
  const { voices } = await api.get<{ voices: SpeechVoice[] }>(
    '/admin/speech/voices',
  );
  return voices;
}

/**
 * The voices offered for one language, best first.
 *
 * Filtered on the language half of the locale, because the catalogue stores
 * ISO 639-1 (`es`) and a voice is named in BCP-47 (`es-ES`). That is also why
 * this filtering matters rather than being a nicety: the provider rejects a
 * request whose language code does not match its voice, so offering an `it-IT`
 * voice on a Spanish scene would produce a scene that never plays.
 */
export function voicesFor(
  voices: SpeechVoice[],
  languageCode: string,
): SpeechVoice[] {
  const wanted = languageCode.trim().toLowerCase().split('-')[0];
  if (!wanted) return [];

  return voices
    .filter((voice) => voice.languageCode.toLowerCase().split('-')[0] === wanted)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** How a voice reads in a picker: the name, then who it sounds like. */
export function describeVoice(voice: SpeechVoice): string {
  return `${voice.name} — ${voice.languageCode}, ${voice.gender}`;
}
